// src/web-app/handlers/pdf-parsing.js
// Web app PDF parsing - uses web-app routes (no bridge mode)
import { renderPages, setupTextSelectionHandler } from '../../common/ui.js';
import { rerenderFootnoteList } from '../../desktop-app/ui/footnote-rendering.js';
import { request } from '../../common/api.js';
import { getAccessToken, getClientId } from '../auth/auth-handler.js';

const pdfParseInflightByDocument = new Map();

export function requestWebAudit(apiBase, path, options = {}) {
  return request(`${apiBase}${path}`, options);
}

function prependInlineParseSuggestion(footnoteListEl, message, btnParseInline) {
  if (!footnoteListEl || !message || !btnParseInline) return;

  footnoteListEl.querySelector('.sparse-footnotes-suggestion, .empty-state-suggestion')?.remove();

  const suggestionDiv = document.createElement('div');
  suggestionDiv.className = 'sparse-footnotes-suggestion';
  suggestionDiv.style.marginBottom = '1rem';
  suggestionDiv.style.padding = '1rem';
  suggestionDiv.style.backgroundColor = '#f0f7ff';
  suggestionDiv.style.border = '1px solid #b8d4f1';
  suggestionDiv.style.borderRadius = '4px';
  suggestionDiv.innerHTML = `
    <p style="margin: 0 0 0.5rem 0; font-size: 0.9rem;">${message}</p>
    <button type="button" class="btn-primary btn-suggest-inline">Parse Inline Citations</button>
  `;

  suggestionDiv.querySelector('.btn-suggest-inline')?.addEventListener('click', () => {
    btnParseInline.click();
  });

  footnoteListEl.prepend(suggestionDiv);
}

/**
 * In-list progress banner for inline citation parsing (web app has no
 * full-screen parse overlay). Mirrors the desktop overlay API so the
 * existing show/updateStatus/updateDetail/updateProgress/hide call sites work.
 */
function createInlineParseProgressBanner(footnoteListEl, statusEl, btnParseInline) {
  let banner = null;
  let titleEl = null;
  let detailEl = null;

  const ensureBanner = () => {
    if (!footnoteListEl) return null;
    if (banner?.isConnected) return banner;
    banner = document.createElement('div');
    banner.className = 'inline-parse-progress';
    banner.style.cssText = 'display:flex;align-items:center;gap:10px;margin-bottom:1rem;padding:1rem;background:#f0f7ff;border:1px solid #b8d4f1;border-radius:4px;';
    banner.innerHTML = `
      <div class="queue-overlay-spinner" aria-hidden="true"></div>
      <div>
        <div class="inline-parse-progress-title" style="font-weight:600;font-size:0.9rem;"></div>
        <div class="inline-parse-progress-detail" style="font-size:0.85rem;color:#4b5563;"></div>
      </div>`;
    titleEl = banner.querySelector('.inline-parse-progress-title');
    detailEl = banner.querySelector('.inline-parse-progress-detail');
    footnoteListEl.prepend(banner);
    return banner;
  };

  const setSuggestionButtonsDisabled = (disabled) => {
    footnoteListEl?.querySelectorAll('.btn-suggest-inline').forEach((btn) => {
      btn.disabled = disabled;
    });
  };

  return {
    show(title, detail) {
      if (!ensureBanner()) return;
      if (titleEl) titleEl.textContent = title || '';
      if (detailEl) detailEl.textContent = detail || '';
      setSuggestionButtonsDisabled(true);
      if (statusEl && title) statusEl.textContent = title;
    },
    updateStatus(title) {
      if (titleEl) titleEl.textContent = title || '';
      if (statusEl && title) statusEl.textContent = title;
    },
    updateDetail(detail) {
      if (detailEl) detailEl.textContent = detail || '';
    },
    updateProgress(done, total) {
      if (statusEl && total > 0) {
        statusEl.textContent = `Parsing inline citations… ${done}/${total} pages`;
      }
    },
    hide() {
      banner?.remove();
      banner = null;
      // Keep suggestion buttons in sync with the real button: after an error
      // it stays disabled by design, and a live-looking suggestion button
      // that silently no-ops is worse than a greyed one.
      setSuggestionButtonsDisabled(!!btnParseInline?.disabled);
    },
  };
}

export async function applyPdfParseResponse({
  responseData,
  statusEl,
  footnoteListEl,
  apiBase,
  documentId,
  mgr,
  updateGenerateReportButton,
  btnParseInline,
  btnVerifyAll
}) {
    const {
      footnotes,
      pages,
      headings,
      cached: ocrWasCached,
      cached_results: preloadedCachedResults,
      inline_parsed: preloadedInlineParsed
    } = responseData;

    console.log('[WEB-APP] === PDF PARSE RESPONSE ===');
    console.log('[WEB-APP] Document ID:', documentId);
    console.log('[WEB-APP] Footnotes received:', footnotes?.length || 0);
    console.log('[WEB-APP] Pages received:', pages?.length || 0);
    console.log('[WEB-APP] Headings received:', headings?.length || 0);
    console.log('[WEB-APP] OCR was cached:', ocrWasCached);
    console.log('[WEB-APP] Sample footnote:', footnotes?.[0]);

    // If OCR was cached, try to fetch previously cached verification results
    let cachedFootnoteResults = {};
    let cachedInlineResults = {};
    let inlineParsed = false;
    if (preloadedCachedResults && typeof preloadedCachedResults === 'object') {
      cachedFootnoteResults = preloadedCachedResults.footnotes || {};
      cachedInlineResults = preloadedCachedResults.inline || {};
      inlineParsed = !!preloadedInlineParsed;
    } else if (ocrWasCached && documentId) {
      try {
        console.log('[WEB-APP] === FETCHING CACHED RESULTS ===');
        console.log('[WEB-APP] Fetching from:', `${apiBase}/web/documents/${encodeURIComponent(documentId)}/cached-results`);
        const cacheResponse = await request(`${apiBase}/web/documents/${encodeURIComponent(documentId)}/cached-results`);
        if (cacheResponse.ok) {
          const cacheData = await cacheResponse.json();
          if (cacheData.status === 'ok' && cacheData.cached_results) {
            // New structure: { footnotes: {...}, inline: {...} }
            cachedFootnoteResults = cacheData.cached_results.footnotes || {};
            cachedInlineResults = cacheData.cached_results.inline || {};
            inlineParsed = cacheData.inline_parsed || false;
            console.log('[WEB-APP] Loaded cached footnote results:', Object.keys(cachedFootnoteResults).length);
            console.log('[WEB-APP] Loaded cached inline page results:', Object.keys(cachedInlineResults).length);
            console.log('[WEB-APP] Inline parsed flag:', inlineParsed);
          }
        }
      } catch (e) {
        console.warn('[WEB-APP] Failed to fetch cached results (continuing):', e);
      }
    }

    // Store in manager - use setter to ensure both items and footnotesCache are set
    mgr.setFootnotes(footnotes || []);
    mgr.pages = pages || [];
    mgr.headings = headings || [];
    mgr.currentDocumentId = documentId; // Set document ID for verification
    
    // If we have cached footnote verification results, inject them into the footnotes
    if (Object.keys(cachedFootnoteResults).length > 0) {
      console.log('[WEB-APP] Injecting cached footnote results...');
      mgr.cachedResults = cachedFootnoteResults;  // Store for UI to access

      // Inject into footnotesCache and resultsCache for immediate display
      for (const fn of mgr.footnotesCache || []) {
        const footnoteId = String(fn.id);
        const referenceId = fn.reference_id ? String(fn.reference_id) : null;
        const cached = cachedFootnoteResults[footnoteId] || (referenceId ? cachedFootnoteResults[referenceId] : null);
        if (cached) {
          if (Array.isArray(cached.parsed_citations) && cached.parsed_citations.length) {
            fn.parsed_citations = cached.parsed_citations;
          }
          // Keep legacy properties for backward compatibility
          fn.cached_congruence = cached.congruence;
          fn.cached_resolve = cached.resolve;

          // Convert to cached_results format expected by the system
          // Each footnote can have multiple citations - iterate through the cached results
          // The backend returns results keyed by citation ID within the footnote
          const citationResults = {};
          const findParsedCitation = (citeId) => (
            (fn.parsed_citations || []).find(c => String(c?.id) === String(citeId))
            || (fn.citations || []).find(c => String(c?.id) === String(citeId))
            || null
          );

          // Check if cached has citation-level results (new format) or is a single result (old format)
          if (cached.citations && typeof cached.citations === 'object') {
            // New format: { citations: { citeId1: {...}, citeId2: {...} } }
            for (const [citeId, citeResult] of Object.entries(cached.citations)) {
              const parsedCitation = citeResult.citation || findParsedCitation(citeId);
              citationResults[citeId] = {
                ...citeResult,
                citation: parsedCitation || citeResult.citation,
                label: citeResult.label || citeResult.congruence?.label,
                calibration: citeResult.calibration || citeResult.congruence?.calibration,
                congruence_result: citeResult.congruence || citeResult,
                resolve_result: citeResult.resolve || cached.resolve,
              };
            }
          } else if (cached.congruence || cached.resolve) {
            // Old format: single citation per footnote, use footnote ID as citation ID
            // Try to get the actual citation ID from parsed_citations if available
            const citeId = fn.parsed_citations?.[0]?.id || `${footnoteId}-cite-1`;
            const parsedCitation = findParsedCitation(citeId) || fn.parsed_citations?.[0] || null;
            citationResults[citeId] = {
              citation: parsedCitation,
              label: cached.congruence?.label,
              calibration: cached.congruence?.calibration,
              rationale: cached.congruence?.rationale,
              congruence_result: cached.congruence,
              resolve_result: cached.resolve,
              file_id: cached.resolve?.file_id,
              file_path: cached.resolve?.file_path,
            };
          }

          if (Object.keys(citationResults).length === 0 && Array.isArray(fn.parsed_citations)) {
            for (const citation of fn.parsed_citations) {
              const citeId = citation?.id ? String(citation.id) : null;
              const citeCached = citeId ? cachedFootnoteResults[citeId] : null;
              if (!citeCached || !(citeCached.congruence || citeCached.resolve)) continue;
              citationResults[citeId] = {
                citation,
                label: citeCached.congruence?.label,
                calibration: citeCached.congruence?.calibration,
                rationale: citeCached.congruence?.rationale,
                congruence_result: citeCached.congruence,
                resolve_result: citeCached.resolve,
                file_id: citeCached.resolve?.file_id,
                file_path: citeCached.resolve?.file_path,
              };
            }
          }

          if (Object.keys(citationResults).length > 0) {
            // Keep any results already supplied with the parsed document. The
            // persisted cache may be partial when citations were resolved at
            // different times.
            fn.cached_results = {
              ...(fn.cached_results && typeof fn.cached_results === 'object' ? fn.cached_results : {}),
              ...citationResults,
            };

            // Store in resultsCache for heading stats and UI rendering
            mgr.resultsCache?.storeResults?.(footnoteId, fn.cached_results);
            console.log(`[WEB-APP] Injected cached result for ${footnoteId}:`,
              Object.values(citationResults).map(r => r.label).join(', '));
          }
        }
      }
    }
    
    // Debug: Log cached inline results state
    console.log('[WEB-APP] Cached inline results check:', {
      cachedInlineResultsKeys: Object.keys(cachedInlineResults).length,
      inlineParsed,
      cachedInlineResults: Object.keys(cachedInlineResults).length > 0 ? cachedInlineResults : '(empty)'
    });

    // Count actual inline citations in cached results
    const cachedInlineCitationCount = Object.values(cachedInlineResults)
      .reduce((count, pageCitations) => count + (Array.isArray(pageCitations) ? pageCitations.length : 0), 0);

    // If we have cached inline results, reconstruct inline citation footnotes and display them
    // Reconstruct if either: (a) inlineParsed flag is true, OR (b) we have actual citation data
    const shouldReconstructInline = Object.keys(cachedInlineResults).length > 0 &&
      (inlineParsed || cachedInlineCitationCount > 0);

    console.log('[WEB-APP] Should reconstruct inline:', shouldReconstructInline,
      '(inlineParsed:', inlineParsed, ', cachedInlineCitationCount:', cachedInlineCitationCount, ')');

    if (shouldReconstructInline) {
      console.log('[WEB-APP] Reconstructing inline citations from cached results...');

      // Normalize cached verification payloads (from /web/documents/{id}/cached-results)
      // into the "cached_results" shape that renderAuditResults expects.
      // The web cache stores resolve/congruence keyed by cite_id (which is the citation.id),
      // so we attach those to the reconstructed inline footnote objects here.
      function normalizeCachedVerificationEntry(cachedEntry, citationId, citation = null) {
        if (!cachedEntry || typeof cachedEntry !== 'object') return null;

        // Newer/alternate format: already has per-citation entries
        if (cachedEntry.citations && typeof cachedEntry.citations === 'object') {
          const out = {};
          for (const [cid, citeResult] of Object.entries(cachedEntry.citations)) {
            out[cid] = {
              ...citeResult,
              citation: citeResult.citation || citation,
              label: citeResult.label || citeResult.congruence?.label || citeResult.congruence_result?.label,
              calibration: citeResult.calibration || citeResult.congruence?.calibration || citeResult.congruence_result?.calibration,
              congruence_result: citeResult.congruence || citeResult.congruence_result,
              resolve_result: citeResult.resolve || citeResult.resolve_result || cachedEntry.resolve,
              // Keep legacy-ish conveniences used around the UI
              method: citeResult.method || citeResult.resolve?.method || citeResult.resolve_result?.method || cachedEntry.resolve?.method,
              url: citeResult.url || citeResult.resolve?.url || citeResult.resolve_result?.url || cachedEntry.resolve?.url,
              file_id: citeResult.file_id || citeResult.file || citeResult.resolve?.file_id || citeResult.resolve_result?.file_id,
            };
          }
          return Object.keys(out).length ? out : null;
        }

        // Web cached-results format today: { congruence: {...}, resolve: {...} }
        const congruence = cachedEntry.congruence || cachedEntry.congruence_result || null;
        const resolve = cachedEntry.resolve || cachedEntry.resolve_result || null;

        // If there's nothing meaningful, don't fabricate
        const hasAnything = !!(congruence?.label || resolve?.method || cachedEntry.label);
        if (!hasAnything) return null;

        const cid = String(citationId);
        return {
          [cid]: {
            // Flatten common fields for convenience
            label: cachedEntry.label || congruence?.label,
            citation: cachedEntry.citation || citation,
            calibration: cachedEntry.calibration ?? congruence?.calibration,
            rationale: cachedEntry.rationale || congruence?.rationale,
            congruence_result: congruence,
            resolve_result: resolve,
            // Quote fidelity: the renderer reads quote_check off the entry, so a
            // reopened document shows the same quote badge a live check does.
            quote_check: cachedEntry.quote_check || congruence?.quote_check || null,
            quote_label: cachedEntry.quote_label || congruence?.quote_label || null,
            method: resolve?.method,
            url: resolve?.url,
            file_id: resolve?.file_id,
            file: resolve?.file_id,
          }
        };
      }

      // Helper to find the most recent heading on or before a given page number
      function findHeadingForPage(pageNum) {
        let bestHeading = null;
        let bestPage = -1;
        for (const heading of (headings || [])) {
          if (heading.page <= pageNum && heading.page > bestPage) {
            bestHeading = heading;
            bestPage = heading.page;
          }
        }
        return bestHeading;
      }

      // Helper to find paragraph containing citation (same as in parse inline handler)
      function findParagraphContaining(text, citationText) {
        if (!text || !citationText) return '';
        const paragraphs = text.split(/\n\n+|\n/);
        for (const para of paragraphs) {
          if (para.includes(citationText)) {
            return para.trim();
          }
        }
        const index = text.indexOf(citationText);
        if (index !== -1) {
          const start = Math.max(0, index - 200);
          const end = Math.min(text.length, index + citationText.length + 200);
          return text.substring(start, end).trim();
        }
        return '';
      }

      // cite_ids already owned by real (non-inline) footnotes — skip these so the
      // same citation isn't stored as both a footnote and an inline result, which
      // would double every count that iterates the resultsCache (document summary).
      const existingFootnoteCiteIds = new Set();
      for (const fn of (mgr.items || [])) {
        if (fn?.is_inline) continue;
        if (fn?.cached_results) {
          for (const cid of Object.keys(fn.cached_results)) existingFootnoteCiteIds.add(String(cid));
        }
        if (Array.isArray(fn?.parsed_citations)) {
          for (const c of fn.parsed_citations) {
            if (c?.id) existingFootnoteCiteIds.add(String(c.id));
          }
        }
      }

      // Reconstruct inline footnotes from cached results
      const inlineFootnotes = [];
      for (const [pageNumStr, citations] of Object.entries(cachedInlineResults)) {
        const pageNum = parseInt(pageNumStr, 10);
        const pageObj = pages?.find(p => (p.page_number ?? pages.indexOf(p) + 1) === pageNum);
        const pageText = pageObj?.main_text || pageObj?.markdown || '';

        (citations || []).forEach((citation, citeIdx) => {
          const citationText = citation.source?.raw || '';
          const context = findParagraphContaining(pageText, citationText) || pageText.substring(0, 500);

          // Use citation.id if available (from cache), otherwise generate one
          const citationId = citation.id || `inline-p${pageNum}-${citeIdx + 1}`;

          // Skip citations already represented as footnote citations (dedup).
          if (existingFootnoteCiteIds.has(String(citationId))) {
            return;
          }

          // Attach cached verification results (resolve/congruence) if present.
          // These are stored under cachedFootnoteResults[cite_id] even for inline citations.
          const cachedVerificationEntry =
            cachedFootnoteResults[String(citationId)] ||
            cachedFootnoteResults[citationId] ||
            null;
          const normalizedCachedResults = normalizeCachedVerificationEntry(cachedVerificationEntry, citationId, {
            ...citation,
            id: citationId,
            footnote_id: citationId,
          });

          const inlineFn = {
            id: citationId,
            text: citationText,
            context: context,
            full_context: context,
            quote: citation.quote || '',
            page_numbers: [pageNum],
            heading_id: citation.heading_id || findHeadingForPage(pageNum)?.id || null,
            reference_id: citationId,
            is_inline: true,
            parsed_citations: [{
              ...citation,
              id: citationId,
              footnote_id: citationId,
            }],
            // Include cached verification results if available (preferred),
            // otherwise do not set cached_results (parse-only inline citations have no labels).
            ...(normalizedCachedResults ? { cached_results: normalizedCachedResults } : {})
          };
          inlineFootnotes.push(inlineFn);
        });
      }

      if (inlineFootnotes.length > 0) {
        console.log(`[WEB-APP] Reconstructed ${inlineFootnotes.length} inline citation footnotes from cache`);

        // Add to mgr.items and mgr.footnotesCache
        mgr.items = [...(mgr.items || []), ...inlineFootnotes];
        if (!Array.isArray(mgr.footnotesCache)) {
          mgr.footnotesCache = [];
        }
        // Merge without duplicating by id
        const byId = new Map((mgr.footnotesCache || []).map(fn => [String(fn.id), fn]));
        for (const fn of inlineFootnotes) {
          byId.set(String(fn.id), fn);
          // Register citation types from parsed inline citations
          if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
            mgr.registerCitationTypes?.(fn.parsed_citations);
          }
        }
        mgr.footnotesCache = Array.from(byId.values());

        // Populate resultsCache with cached_results from inline footnotes
        for (const footnote of inlineFootnotes) {
          if (footnote.cached_results && Object.keys(footnote.cached_results).length > 0) {
            const footnoteId = String(footnote.id);
            console.log(`[WEB-APP] Storing cached_results for inline footnote ${footnoteId}`);
            mgr.resultsCache?.storeResults?.(footnoteId, footnote.cached_results);
          }
        }
      }

      // Store for reference
      mgr.cachedInlineResults = cachedInlineResults;
    }

    // Always store cached inline results for reference (even if empty)
    if (Object.keys(cachedInlineResults).length > 0) {
      mgr.cachedInlineResults = cachedInlineResults;
    }

    // setFootnotes() ran before cached parsed citations were attached above.
    // Re-index now that both restored footnote and inline citations are present,
    // preserving the user's Show/Verify selections while rebuilding counts.
    mgr.refreshCitationTypes?.();
    
    // Update Generate HTML Report button state after parsing
    if (updateGenerateReportButton) {
      updateGenerateReportButton();
    }
    
    console.log('[WEB-APP] mgr.footnotesCache after setFootnotes:', mgr.footnotesCache?.length);

    console.log('[WEB-APP] mgr.items after assignment:', mgr.items.length);
    console.log('[WEB-APP] footnoteListEl:', footnoteListEl);
    console.log('[WEB-APP] footnoteListEl exists?', !!footnoteListEl);
    console.log('[WEB-APP] footnoteListEl parent:', footnoteListEl?.parentElement);
    console.log('[WEB-APP] footnoteListEl visible?', footnoteListEl ? window.getComputedStyle(footnoteListEl).display !== 'none' : 'N/A');

    if (!footnoteListEl) {
      console.error('[WEB-APP] ERROR: footnoteListEl is null! Cannot render footnotes.');
      if (statusEl) statusEl.textContent = 'Error: Footnote list element not found.';
      return;
    }

    footnoteListEl.innerHTML = ``;
    
    // Reset citation category filters when loading a new document.
    if (typeof window !== 'undefined' && window.clearCitationTabs) {
      window.clearCitationTabs();
    }

    // Render with the exact same hierarchical logic as Desktop App
    const totalFootnotes = (mgr.items || []).length;

    // Check if footnotes are fewer than pages
    const totalPages = pages?.length || 0;
    const isSparse = totalPages > 0 && totalFootnotes < totalPages;

    // Disable parse inline button if already parsed
    if (btnParseInline) {
      if (inlineParsed) {
        btnParseInline.dataset.inlineParsed = 'true';
        btnParseInline.disabled = true;
        btnParseInline.title = 'Inline citations have already been parsed for this document';
        console.log('[WEB-APP] Disabled parse inline button - already parsed');
      } else {
        btnParseInline.dataset.inlineParsed = 'false';
        btnParseInline.disabled = false;
        btnParseInline.title = '';
      }
    }

    // Check if we have anything to render
    if (totalFootnotes === 0) {
      console.log('[WEB-APP] No footnotes to render');
      if (footnoteListEl) {
        footnoteListEl.innerHTML = `<div class="empty-state">✅ Process complete. No citations were found.</div>`;

        // If no footnotes found and document has pages, suggest inline citation parsing
        // Only show the button if inline parsing hasn't been done yet
        if (isSparse && !inlineParsed) {
          prependInlineParseSuggestion(
            footnoteListEl,
            'This document may use inline citations instead of citations in notes.',
            btnParseInline
          );
        }
      }
    } else {
      console.log('[WEB-APP] Rendering footnotes with desktop-aligned hierarchical renderer...');
      rerenderFootnoteList(mgr, footnoteListEl);
      mgr.applyCitationTypeVisibility?.();

      // If footnotes are sparse, add suggestion to parse inline citations
      // Only show if inline parsing hasn't been done yet
      if (isSparse && !inlineParsed && footnoteListEl) {
        prependInlineParseSuggestion(
          footnoteListEl,
          `<strong>Note:</strong> Only ${totalFootnotes} citation${totalFootnotes === 1 ? '' : 's'} found in notes across ${totalPages} pages. This document may use inline citations.`,
          btnParseInline
        );
      }

      console.log('[WEB-APP] Footnote rendering complete. footnoteListEl children:', footnoteListEl?.children.length);
      console.log('[WEB-APP] footnoteListEl HTML length:', footnoteListEl?.innerHTML.length);

      // Mark rendered cards with categories from cached results after rerender.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (mgr?.resultsCache && typeof window.updateCitationTabs === 'function') {
              console.log('[WEB-APP] Marking citation cards from cached results...');
              for (const [footnoteId, footnoteResults] of mgr.resultsCache.cache.entries()) {
                if (!footnoteResults || typeof footnoteResults !== 'object') continue;
                for (const [citationId, result] of Object.entries(footnoteResults)) {
                  if (citationId === '__error__') continue;
                  if (result && result.label) {
                    window.updateCitationTabs(footnoteId, citationId, result);
                  }
                }
              }
            }
          }, 150);
        });
      });
    }

    renderPages(mgr.pages, null, { pdfBytes: mgr.state?.fileBytes });
    
    // Set up text selection handler for manual citation parsing
    setupTextSelectionHandler({
      onParseSelection: async (selectionData) => {
        const citation = await mgr.addManualCitation(selectionData, () => {
          rerenderFootnoteList(mgr, footnoteListEl);
          window.applyCitationTabFilter?.();
        });
        if (citation) {
          console.log('[WEB-APP] Manual citation added:', citation.id);
        }
      },
      getPageText: (pageNum) => mgr.getPageText(pageNum),
    });
    
    // Update status message based on cache state
    const cachedFootnoteCount = Object.keys(cachedFootnoteResults).length;
    const cachedInlinePageCount = Object.keys(cachedInlineResults).length;
    const loadedInlineCitationCount = (mgr.items || []).filter(item => item?.is_inline).length;
    const totalCached = cachedFootnoteCount + cachedInlinePageCount;
    
    if (ocrWasCached && totalCached > 0) {
      const parts = [];
      if (cachedFootnoteCount > 0) parts.push(`${cachedFootnoteCount} citation verifications`);
      if (loadedInlineCitationCount > 0) {
        parts.push(`${loadedInlineCitationCount} inline citations`);
      } else if (cachedInlinePageCount > 0) {
        parts.push(`${cachedInlinePageCount} inline pages`);
      }
      if (statusEl) statusEl.textContent = `⚡ Loaded from cache. ${parts.join(', ')} restored.`;
    } else if (ocrWasCached) {
      if (statusEl) statusEl.textContent = '⚡ OCR loaded from cache. Ready for verification.';
    } else {
      if (statusEl) statusEl.textContent = 'Done. OCR results cached for next time.';
    }
    
    // Keep parse inline button disabled if already parsed, otherwise enable
    if (btnParseInline) {
      btnParseInline.dataset.inlineParsed = inlineParsed ? 'true' : 'false';
      btnParseInline.disabled = inlineParsed;
      btnParseInline.title = inlineParsed
        ? 'Inline citations have already been parsed for this document'
        : '';
    }
    if (btnVerifyAll) btnVerifyAll.disabled = false;
}

// Extract parsing logic into a reusable function
export async function parsePdfFootnotes({
  fileInput,
  statusEl,
  footnoteListEl,
  apiBase,
  getPicked,
  getDocumentId,
  mgr,
  updateGenerateReportButton,
  btnParseInline,
  btnVerifyAll
}) {
  const picked = getPicked();
  const documentId = getDocumentId();

  // Parse lifecycle events drive the queue card overlay (see queue-manager.js)
  const dispatchParseEvent = (name, detail = {}) => {
    window.dispatchEvent(new CustomEvent(name, {
      detail: { documentId, fileName: picked?.name || null, ...detail }
    }));
  };

  if (!picked) {
    if (fileInput) fileInput.click();
    return;
  }

  if (!picked.bytes) {
    if (statusEl) statusEl.textContent = 'Please pick the original PDF to re-parse this saved document.';
    return;
  }

  if (documentId && pdfParseInflightByDocument.has(documentId)) {
    if (statusEl) statusEl.textContent = 'OCR is still processing for this document. Results will appear when it finishes.';
    if (footnoteListEl) footnoteListEl.innerHTML = `<li>OCR pending for this document... ⏳</li>`;
    return pdfParseInflightByDocument.get(documentId);
  }

  dispatchParseEvent('fc:parse-started');

  const parsePromise = (async () => {
    if (statusEl) statusEl.textContent = 'Parsing PDF citations…';
    if (footnoteListEl) footnoteListEl.innerHTML = `<li>Performing OCR and extracting citations... ⏳</li>`;
    if (documentId && mgr) {
      if (mgr.currentDocumentId !== documentId) {
        mgr.clearState?.();
      }
      if (typeof mgr.setDocumentId === 'function') {
        mgr.setDocumentId(documentId);
      } else {
        mgr.currentDocumentId = documentId;
      }
    }

    // Build FormData for file upload
    const file = new File([picked.bytes], picked.name, { type: "application/pdf" });
    if (mgr?.state) {
      mgr.state.fileBytes = picked.bytes;
      mgr.state.documentId = documentId;
      mgr.state.documentPath = "";
    }

    const form = new FormData();
    form.append("file", file);
    form.append("document_id", documentId);
    form.append("document_path", ""); // Web app: no document path

    // Call web-app endpoint (request function will auto-inject auth token)
    const response = await request(`${apiBase}/web/pdf/footnotes_md`, {
      method: "POST",
      body: form
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    }

    const responseData = await response.json();
    console.log('[WEB-APP] Response data:', responseData);

    if (!responseData?.cached) {
      dispatchParseEvent('fc:parse-progress', {
        title: 'Building citation view…',
        detail: 'Applying headings, cached results, and page previews.'
      });
    }

    if (documentId && getDocumentId() && getDocumentId() !== documentId) {
      console.warn('[WEB-APP] Ignoring stale PDF parse response for document:', documentId);
      return responseData;
    }

    await applyPdfParseResponse({
      responseData,
      statusEl,
      footnoteListEl,
      apiBase,
      documentId,
      mgr,
      updateGenerateReportButton,
      btnParseInline,
      btnVerifyAll
    });
    return responseData;
  })();

  if (documentId) {
    pdfParseInflightByDocument.set(documentId, parsePromise);
  }

  try {
    const responseData = await parsePromise;
    dispatchParseEvent('fc:parse-finished', { cached: !!responseData?.cached });
    return responseData;
  } catch (err) {
    console.error(err);
    if (statusEl) statusEl.textContent = 'Failed to parse PDF: ' + (err.message || err);
    dispatchParseEvent('fc:parse-error', { message: err.message || String(err) });
    throw err;
  } finally {
    if (documentId) {
      pdfParseInflightByDocument.delete(documentId);
    }
  }
}

export function setupPdfParsing({
  btnParse,
  btnParseInline,
  btnVerifyAll,
  fileInput,
  statusEl,
  footnoteListEl,
  apiBase,
  getPicked,
  getDocumentId,
  mgr,
  signal,
  updateGenerateReportButton
}) {
  
  // Keep button handler for backward compatibility (button removed from UI but code still works)
  btnParse?.addEventListener('click', async (e) => {
    e.preventDefault();
    await parsePdfFootnotes({
      fileInput,
      statusEl,
      footnoteListEl,
      apiBase,
      getPicked,
      getDocumentId,
      mgr,
      updateGenerateReportButton,
      btnParseInline,
      btnVerifyAll
    });
  }, { signal });

  // Parse inline citations button handler
  btnParseInline?.addEventListener('click', async (e) => {
    e.preventDefault();
    const documentId = getDocumentId();

    if (!mgr.pages || mgr.pages.length === 0) {
      if (statusEl) statusEl.textContent = 'Please parse PDF citations first.';
      // Clean up queue callback if PDF not parsed yet
      if (window.__FC_QUEUE_INLINE_CALLBACK__) {
        delete window.__FC_QUEUE_INLINE_CALLBACK__;
      }
      return;
    }
    
    const batchProgress = window.__FC_BATCH_PROGRESS__;
    // Batch runs report via the status pill; interactive runs get an in-list
    // progress banner (inline parsing takes 1-3 minutes — it must be visible).
    const overlay = window.__FC_BATCH_MODE__
      ? null
      : createInlineParseProgressBanner(footnoteListEl, statusEl, btnParseInline);

    try {
      btnParseInline.disabled = true;

      const totalPages = mgr.pages.length;
      if (overlay) {
        overlay.show('Parsing inline citations…', `Sending ${totalPages} pages to server...`);
        // Initialize progress tracking - show 0/totalPages initially
        overlay.updateProgress(0, totalPages);
      } else if (batchProgress) {
        batchProgress('parsing', 0, totalPages);
      }

      // Prepare pages array (extract main_text from pages)
      const batchPages = mgr.pages.map((page, idx) => {
        const prevPage = mgr.pages[idx - 1];
        let prevPageText = prevPage ? (prevPage.main_text || prevPage.markdown || '') : '';
        if (prevPageText.length > 600) prevPageText = prevPageText.slice(-600);
        return {
          page_number: page.page_number || (idx + 1),
          text: page.main_text || page.markdown || '',
          context: page.markdown || '',
          prev_context: prevPageText || null,
          rich_text: page.rich_text || null,
          reference_id: `p${page.page_number || (idx + 1)}`
        };
      });

      if (overlay) {
        overlay.updateDetail(`Processing ${batchPages.length} pages on server (this may take 1-3 minutes)...`);
        overlay.updateProgress(0, batchPages.length); // Start at 0
      }

      // Call web-app endpoint with Server-Sent Events for real-time progress
      const response = await request(`${apiBase}/web/audit/parse_inline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          pages: batchPages,
          document_id: documentId,
          document_path: null, // Web app: no document path
          data_source: 'internet' // Web app: internet only
          // No client_id in body = no bridge mode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      // Process Server-Sent Events stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let batchResults = [];
      let cachedCount = 0;
      let freshCount = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'progress') {
                // Update progress in real-time
                if (overlay) {
                  overlay.updateProgress(data.completed, data.total);
                  overlay.updateDetail(`Processing pages: ${data.completed}/${data.total} completed`);
                } else if (batchProgress) {
                  batchProgress('parsing', data.completed, data.total);
                }
              } else if (data.type === 'complete') {
                // Final results
                batchResults = data.results || [];
                cachedCount = data.cached_count || 0;
                freshCount = data.fresh_count || 0;
              }
            } catch (e) {
              console.error('[WEB-APP] Failed to parse SSE data:', e);
            }
          }
        }
      }
      
      if (batchResults.length === 0) {
        throw new Error('No results returned from server');
      }

      if (documentId && mgr?.currentDocumentId && String(mgr.currentDocumentId) !== String(documentId)) {
        console.warn('[WEB-APP] Ignoring stale inline parse response for document:', documentId);
        return;
      }

      // Show completion message
      const cacheMsg = cachedCount > 0 ? ` (${cachedCount} from cache, ${freshCount} newly processed)` : '';
      if (overlay) {
        overlay.updateDetail(`Completed! ${batchResults.length} pages processed${cacheMsg}`);
        overlay.updateProgress(batchResults.length, batchResults.length);
      }

      // Process results and integrate inline citations into footnote list
      console.log('[WEB-APP] Inline parsing results:', batchResults);

      // Helper to find the most recent heading on or before a given page number
      function findHeadingForPage(pageNum) {
        let bestHeading = null;
        let bestPage = -1;
        for (const heading of (mgr.headings || [])) {
          if (heading.page <= pageNum && heading.page > bestPage) {
            bestHeading = heading;
            bestPage = heading.page;
          }
        }
        return bestHeading;
      }

      // Convert inline citations to footnote-like objects and add to mgr.items
      const inlineFootnotes = [];
      let errorCount = 0;
      
      for (const result of batchResults) {
        if (result.status !== 'ok') {
          console.error(`[WEB-APP] Error parsing page ${result.page_number}:`, result.error);
          errorCount++;
          continue;
        }
        
        const citations = result.citations || [];
        const pageNum = result.page_number;
        const pageObj = mgr.pages.find(p => (p.page_number ?? mgr.pages.indexOf(p) + 1) === pageNum);
        const pageText = pageObj?.main_text || pageObj?.markdown || '';
        
        // Helper to find paragraph containing citation
        function findParagraphContaining(text, citationText) {
          if (!text || !citationText) return '';
          const paragraphs = text.split(/\n\n+|\n/);
          for (const para of paragraphs) {
            if (para.includes(citationText)) {
              return para.trim();
            }
          }
          const index = text.indexOf(citationText);
          if (index !== -1) {
            const start = Math.max(0, index - 200);
            const end = Math.min(text.length, index + citationText.length + 200);
            return text.substring(start, end).trim();
          }
          return '';
        }
        
        // Create footnote-like objects for each inline citation
        citations.forEach((citation, citeIdx) => {
          const citationText = citation.source?.raw || '';
          const context = findParagraphContaining(pageText, citationText) || pageText.substring(0, 500);
          
          const inlineFn = {
            id: `inline-p${pageNum}-${citeIdx + 1}`,
            text: citationText,
            context: context,
            full_context: context,
            quote: citation.quote || '',
            page_numbers: [pageNum],
            heading_id: findHeadingForPage(pageNum)?.id || null,
            reference_id: `inline-p${pageNum}-${citeIdx + 1}`,
            is_inline: true,
            parsed_citations: [citation]
          };
          inlineFootnotes.push(inlineFn);
        });
      }
      
      console.log(`[WEB-APP] Created ${inlineFootnotes.length} inline citation footnotes`);
      
      // Show completion status
      const errorMsg = errorCount > 0 ? `, ${errorCount} errors` : '';
      if (overlay) {
        overlay.updateStatus('Done!');
        overlay.updateDetail(`Found ${inlineFootnotes.length} inline citations${errorMsg}.`);
      }
      
      // Add inline footnotes to mgr.items and ensure cache stays in sync
      mgr.items = [...(mgr.items || []), ...inlineFootnotes];
      if (!Array.isArray(mgr.footnotesCache)) {
        mgr.footnotesCache = [];
      }
      // Merge without duplicating by id
      const byId = new Map((mgr.footnotesCache || []).map(fn => [String(fn.id), fn]));
      for (const fn of inlineFootnotes) {
        byId.set(String(fn.id), fn);
        // Register citation types from parsed inline citations
        if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
          mgr.registerCitationTypes?.(fn.parsed_citations);
        }
      }
      mgr.footnotesCache = Array.from(byId.values());
      
      // Update Generate HTML Report button state after adding inline citations
      if (updateGenerateReportButton) {
        updateGenerateReportButton();
      }

      // Inline parsing is now complete, so the suggestion banner should disappear
      footnoteListEl?.querySelectorAll('.sparse-footnotes-suggestion, .empty-state-suggestion')
        .forEach((el) => el.remove());
      
      // Re-render the full list (desktop-aligned grouping, including inline citations)
      if (footnoteListEl && inlineFootnotes.length > 0) {
        console.log('[WEB-APP] Re-rendering footnote list with inline citations...');
        rerenderFootnoteList(mgr, footnoteListEl);
        window.applyCitationTabFilter?.();
      }

      // Disable the parse inline button and add tooltip (will also be disabled on next load via flag)
      btnParseInline.dataset.inlineParsed = 'true';
      btnParseInline.disabled = true;
      btnParseInline.title = 'Inline citations have already been parsed for this document';
      console.log('[WEB-APP] Disabled parse inline button after successful parse');

      // Call queue callback if this was initiated from the queue Go button
      // This triggers verification after inline parsing completes
      if (typeof window.__FC_QUEUE_INLINE_CALLBACK__ === 'function') {
        console.log('[WEB-APP] Calling queue inline callback to trigger verification');
        window.__FC_QUEUE_INLINE_CALLBACK__();
      }

      // Keep overlay visible longer so user can see completion
      if (overlay) {
        setTimeout(() => overlay.hide(), 3000); // Increased to 3s
      }
      if (statusEl) statusEl.textContent = `Parsed ${batchResults.length} pages, found ${inlineFootnotes.length} inline citations.`;
    } catch (err) {
      console.error(err);
      if (statusEl) statusEl.textContent = 'Failed to parse inline citations: ' + (err.message || err);
      if (overlay) {
        overlay.hide();
      }
      // Clean up queue callback on error
      if (window.__FC_QUEUE_INLINE_CALLBACK__) {
        delete window.__FC_QUEUE_INLINE_CALLBACK__;
      }
      // Don't re-enable button on error - user can reload to try again
    } finally {
      // Don't re-enable button here - it's intentionally left disabled after successful parse
    }
  }, { signal });

  // Verify All Citations button handler
  btnVerifyAll?.addEventListener('click', async (e) => {
    e.preventDefault();
    
    if (!mgr.footnotesCache || mgr.footnotesCache.length === 0) {
      if (statusEl) statusEl.textContent = 'Please parse PDF citations first.';
      return;
    }
    
    try {
      btnVerifyAll.disabled = true;
      // Web app uses Cloud Run which handles parallelization server-side
      // We can use higher concurrency since browser connection pool isn't the bottleneck
      // (Cloud Run + HTTP/2 handles concurrent requests efficiently)
      await mgr.verifyAllSmart({ parse: 12, resolve: 12, analyze: 12 });
    } catch (err) {
      console.error('[WEB-APP] Verify All error:', err);
      if (statusEl) statusEl.textContent = 'Error verifying citations: ' + (err.message || err);
    } finally {
      btnVerifyAll.disabled = false;
    }
  }, { signal });
}
